import React, { useState } from "react";
import { BadgeCheck, FileUp, Plus, Sparkles, Trash2, UserCheck, UserPlus, X } from "lucide-react";
import { practiceApi, euro } from "../../lib/practiceApi";
import { newRowId, withRowIds } from "../../lib/rowKeys";

const CONFIDENCE = { high: ["Riconosciuto", "success"], medium: ["Da verificare", "warning"], low: ["Non individuato", "neutral"] };
const CLIENT_FIELDS = [["name", "Ragione sociale / nominativo"], ["fiscal_code", "Codice fiscale"], ["vat_number", "Partita IVA"],
                       ["address", "Indirizzo"], ["email", "Email"], ["phone", "Telefono"]];
const ENGAGEMENT_FIELDS = [["number", "Numero preventivo"], ["date", "Data"], ["subject", "Oggetto"],
                           ["municipality", "Comune"], ["site_address", "Ubicazione"]];
const AMOUNT_FIELDS = [["taxable", "Imponibile"], ["discount", "Sconto"], ["social_contribution", "Contributo previdenziale"],
                       ["vat", "IVA"], ["expenses", "Spese"], ["total", "Totale"], ["advance", "Anticipo"]];
const CONDITION_FIELDS = [["payment_terms", "Modalità di pagamento"], ["installments", "Scadenze di pagamento"],
                          ["validity", "Validità dell'offerta"], ["execution_times", "Tempi di esecuzione"],
                          ["included", "Prestazioni comprese"], ["excluded", "Prestazioni escluse"],
                          ["notes", "Note"], ["clauses", "Clausole e condizioni particolari"]];

const Confidence = ({ level, testId }) => {
  const [label, tone] = CONFIDENCE[String(level || "").toLowerCase()] || CONFIDENCE.low;
  return <span className={`practice-badge ${tone}`} data-testid={testId}>{label}</span>;
};
const rowTotal = row => (Number(row.quantity) || 0) && (Number(row.unit_price) || 0)
  ? (Number(row.quantity) * Number(row.unit_price)) - (Number(row.discount) || 0)
  : (Number(row.amount) || 0) - (Number(row.discount) || 0);

export const QuoteImport = ({ clients, onClose, onCreated }) => {
  const [step, setStep] = useState("upload");
  const [source, setSource] = useState(null);
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState([]);
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const section = (name, patch) => setData(d => ({ ...d, [name]: { ...(d[name] || {}), ...patch } }));
  const setItem = (index, patch) => setData(d => ({ ...d, items: d.items.map((row, i) => i === index ? { ...row, ...patch } : row) }));
  const moveItem = (index, delta) => setData(d => {
    const items = [...d.items];
    const target = index + delta;
    if (target < 0 || target >= items.length) return d;
    [items[index], items[target]] = [items[target], items[index]];
    return { ...d, items };
  });

  async function upload(file) {
    setBusy("upload"); setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const result = await practiceApi("/estimates/import/upload", { method: "POST", body });
      setSource(result); setStep("analyze");
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function analyze() {
    setBusy("analyze"); setError("");
    try {
      const result = await practiceApi(`/estimates/import/${source.id}/analyze`, { method: "POST" });
      const extraction = result.extraction || {};
      setData({ client: extraction.client || {}, engagement: extraction.engagement || {}, items: withRowIds(extraction.items),
                expenses: extraction.expenses || [], amounts: extraction.amounts || {}, conditions: extraction.conditions || {} });
      setMatches(result.client_matches || []);
      setClientId((result.client_matches || [])[0]?.id || "");
      setStep("review");
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function linkExisting() {
    setBusy("client"); setError(""); setNotice("");
    try {
      const result = await practiceApi(`/estimates/import/${source.id}/client`, { method: "POST", body: JSON.stringify({ client_id: clientId }) });
      setNotice(`Cliente associato: ${result.client.name}`);
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function createClient() {
    setBusy("client"); setError(""); setNotice("");
    try {
      const result = await practiceApi(`/estimates/import/${source.id}/client`, { method: "POST", body: JSON.stringify({ create: true, data: data.client }) });
      if (!result.created) {
        setMatches(result.duplicates || []);
        setClientId((result.duplicates || [])[0]?.id || "");
        setNotice("Anagrafica già presente: associa il cliente esistente per evitare duplicazioni");
      } else {
        setClientId(result.client.id);
        setNotice(`Nuovo cliente creato: ${result.client.name}`);
      }
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }
  async function convert() {
    setBusy("convert"); setError("");
    try {
      const result = await practiceApi(`/estimates/import/${source.id}/convert`, { method: "POST", body: JSON.stringify({ data }) });
      onCreated(result.quote_id);
    } catch (err) { setError(err.message); } finally { setBusy(""); }
  }

  const itemsTotal = (data?.items || []).reduce((sum, row) => sum + rowTotal(row), 0);

  return <section className="quote-import" data-testid="quote-import">
    <div className="practice-action-bar">
      <button className="ghost" data-testid="quote-import-close" onClick={onClose}>← Tutti i preventivi</button>
      <span className="muted">{source ? source.filename : "Importa un preventivo esistente"}</span>
    </div>
    {error && <div className="error" role="alert" data-testid="quote-import-error">{error}</div>}
    {notice && <div className="practice-notice" data-testid="quote-import-notice">{notice}</div>}

    {step === "upload" && <div className="quote-import-drop">
      <label className="practice-drop" data-testid="quote-import-upload-label">
        <FileUp size={26} />
        <strong>{busy === "upload" ? "Caricamento…" : "Carica preventivo (PDF, Word .doc o .docx)"}</strong>
        <span>Il documento originale resta sempre archiviato come allegato</span>
        <input type="file" hidden accept=".pdf,.doc,.docx" disabled={!!busy} data-testid="quote-import-file"
          onChange={e => { const file = e.target.files[0]; e.target.value = ""; if (file) upload(file); }} />
      </label>
    </div>}

    {step === "analyze" && <div className="quote-import-analyze" data-testid="quote-import-analyze-step">
      <p>Documento caricato: <strong>{source.filename}</strong> · {source.characters} caratteri estratti.</p>
      <button className="primary" data-testid="quote-import-analyze" disabled={!!busy} onClick={analyze}>
        <Sparkles size={15} />{busy === "analyze" ? "Analisi AI in corso…" : "Analizza con AI"}
      </button>
    </div>}

    {step === "review" && data && <div className="quote-import-review" data-testid="quote-import-review">
      <header className="practice-section-heading"><h2><BadgeCheck size={17} />Dati riconosciuti dal documento</h2>
        <span>Controlla e correggi ogni campo: nessun dato diventa definitivo senza la tua conferma</span></header>

      <fieldset className="practice-fieldset"><legend>Cliente <Confidence level={data.client.confidence} testId="quote-import-client-confidence" /></legend>
        <div className="practice-form-grid">
          {CLIENT_FIELDS.map(([key, label]) => <label key={key}>{label}
            <input data-testid={`quote-import-client-${key}`} value={data.client[key] || ""} onChange={e => section("client", { [key]: e.target.value })} />
          </label>)}
        </div>
        <div className="quote-import-client-actions">
          <select data-testid="quote-import-client-match" value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">Nessun cliente selezionato</option>
            {matches.map(m => <option key={m.id} value={m.id}>{m.name} · corrispondenza per {m.match}</option>)}
            {clients.filter(c => !matches.some(m => m.id === c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="ghost" data-testid="quote-import-client-link" disabled={!clientId || !!busy} onClick={linkExisting}><UserCheck size={14} />Associa al cliente esistente</button>
          <button className="ghost" data-testid="quote-import-client-create" disabled={!!busy} onClick={createClient}><UserPlus size={14} />Crea nuovo cliente</button>
        </div>
      </fieldset>

      <fieldset className="practice-fieldset"><legend>Incarico <Confidence level={data.engagement.confidence} testId="quote-import-engagement-confidence" /></legend>
        <div className="practice-form-grid">
          {ENGAGEMENT_FIELDS.map(([key, label]) => <label key={key} className={key === "subject" ? "field-wide" : undefined}>{label}
            <input data-testid={`quote-import-engagement-${key}`} value={data.engagement[key] || ""} onChange={e => section("engagement", { [key]: e.target.value })} />
          </label>)}
          <label className="field-wide">Descrizione dell'incarico
            <textarea rows={3} data-testid="quote-import-engagement-description" value={data.engagement.description || ""} onChange={e => section("engagement", { description: e.target.value })} />
          </label>
        </div>
      </fieldset>

      <fieldset className="practice-fieldset"><legend>Prestazioni individuate</legend>
        {data.items.map((row, index) => <div className="quote-import-row" key={row._uid} data-testid={`quote-import-item-${index}`}>
          <input className="quote-import-label" placeholder="Descrizione" data-testid={`quote-import-item-label-${index}`} value={row.label || ""} onChange={e => setItem(index, { label: e.target.value })} />
          <input type="number" step="0.01" placeholder="Q.tà" data-testid={`quote-import-item-quantity-${index}`} value={row.quantity ?? ""} onChange={e => setItem(index, { quantity: e.target.value })} />
          <input type="number" step="0.01" placeholder="Prezzo unitario" data-testid={`quote-import-item-unit-${index}`} value={row.unit_price ?? ""} onChange={e => setItem(index, { unit_price: e.target.value })} />
          <input type="number" step="0.01" placeholder="Importo" data-testid={`quote-import-item-amount-${index}`} value={row.amount ?? ""} onChange={e => setItem(index, { amount: e.target.value })} />
          <input type="number" step="0.01" placeholder="Sconto" data-testid={`quote-import-item-discount-${index}`} value={row.discount ?? ""} onChange={e => setItem(index, { discount: e.target.value })} />
          <input placeholder="Note" data-testid={`quote-import-item-notes-${index}`} value={row.notes || ""} onChange={e => setItem(index, { notes: e.target.value })} />
          <strong data-testid={`quote-import-item-total-${index}`}>{euro(rowTotal(row))}</strong>
          <div className="quote-import-row-actions">
            <button className="icon-btn" title="Sposta su" data-testid={`quote-import-item-up-${index}`} onClick={() => moveItem(index, -1)}>↑</button>
            <button className="icon-btn" title="Sposta giù" data-testid={`quote-import-item-down-${index}`} onClick={() => moveItem(index, 1)}>↓</button>
            <button className="icon-btn" title="Elimina voce" data-testid={`quote-import-item-remove-${index}`} onClick={() => setData(d => ({ ...d, items: d.items.filter((_, i) => i !== index) }))}><Trash2 size={13} /></button>
          </div>
        </div>)}
        <button className="ghost" data-testid="quote-import-item-add" onClick={() => setData(d => ({ ...d, items: [...d.items, { _uid: newRowId(), label: "", quantity: "", unit_price: "", amount: "", discount: "", notes: "" }] }))}><Plus size={14} />Aggiungi voce</button>
        <p className="practice-hint" data-testid="quote-import-items-total">Totale voci: <strong>{euro(itemsTotal)}</strong></p>
      </fieldset>

      <fieldset className="practice-fieldset"><legend>Importi <Confidence level={data.amounts.confidence} testId="quote-import-amounts-confidence" /></legend>
        <div className="practice-form-grid">
          {AMOUNT_FIELDS.map(([key, label]) => <label key={key}>{label}
            <input type="number" step="0.01" data-testid={`quote-import-amount-${key}`} value={data.amounts[key] ?? ""} onChange={e => section("amounts", { [key]: e.target.value })} />
          </label>)}
        </div>
        <p className="practice-hint">Contributo previdenziale e IVA vengono applicati nel preventivo INGEGO solo se presenti in questo riquadro.</p>
      </fieldset>

      <fieldset className="practice-fieldset"><legend>Condizioni <Confidence level={data.conditions.confidence} testId="quote-import-conditions-confidence" /></legend>
        <div className="practice-form-grid">
          {CONDITION_FIELDS.map(([key, label]) => <label key={key} className="field-wide">{label}
            <textarea rows={2} data-testid={`quote-import-condition-${key}`} value={data.conditions[key] || ""} onChange={e => section("conditions", { [key]: e.target.value })} />
          </label>)}
        </div>
      </fieldset>

      <div className="practice-inline-actions quote-import-footer">
        <button className="ghost" data-testid="quote-import-cancel" onClick={onClose}><X size={15} />Annulla</button>
        <button className="primary" data-testid="quote-import-generate" disabled={!!busy} onClick={convert}>
          <Sparkles size={15} />{busy === "convert" ? "Generazione…" : "Genera preventivo INGEGO"}
        </button>
      </div>
    </div>}
  </section>;
};
